import { html, nothing } from "lit";
import { repeat } from "lit/directives/repeat.js";
import type { AppViewState } from "./app-view-state.ts";
import type { UsageState } from "./controllers/usage.ts";
import { parseAgentSessionKey } from "../../../src/routing/session-key.js";
import { refreshChatAvatar } from "./app-chat.ts";
import { makeSwitchSession, renderChatControls, renderTab, renderThemeToggle } from "./app-render.helpers.ts";
import { loadAgentFileContent, loadAgentFiles, saveAgentFile } from "./controllers/agent-files.ts";
import { loadAgentIdentities, loadAgentIdentity } from "./controllers/agent-identity.ts";
import { loadAgentSkills } from "./controllers/agent-skills.ts";
import { loadAgents } from "./controllers/agents.ts";
import { loadChannels } from "./controllers/channels.ts";
import { loadChatHistory } from "./controllers/chat.ts";
import {
  applyConfig,
  loadConfig,
  runUpdate,
  saveConfig,
  updateConfigFormValue,
  removeConfigFormValue,
} from "./controllers/config.ts";
import {
  loadCronRuns,
  toggleCronJob,
  runCronJob,
  removeCronJob,
  addCronJob,
} from "./controllers/cron.ts";
import { loadDebug, callDebugMethod } from "./controllers/debug.ts";
import {
  approveDevicePairing,
  loadDevices,
  rejectDevicePairing,
  revokeDeviceToken,
  rotateDeviceToken,
} from "./controllers/devices.ts";
import {
  loadExecApprovals,
  removeExecApprovalsFormValue,
  saveExecApprovals,
  updateExecApprovalsFormValue,
} from "./controllers/exec-approvals.ts";
import { loadLogs } from "./controllers/logs.ts";
import { loadNodes } from "./controllers/nodes.ts";
import { loadAgentPlans } from "./controllers/agent-plans.ts";
import { loadSystemOverview } from "./controllers/system-overview.ts";
import {
  loadInferenceConfig,
  saveInferenceConfig,
  updateModelPricing,
  updateMaintenanceFee,
  updateSelectedPlan,
  updateExtraPerToken,
  saveBillingConfig,
} from "./controllers/inference-config.ts";
import { loadBillingSummary, downloadInvoice } from "./controllers/billing.ts";
import {
  loadTabVisibility,
  saveTabVisibility,
  toggleTabVisible,
} from "./controllers/tab-visibility.ts";
import {
  loadRoleSettings,
  saveRoleSettings,
  setRoleSettingValue,
} from "./controllers/role-settings.ts";
import { loadPresence } from "./controllers/presence.ts";
import { deleteSession, loadSessions, patchSession } from "./controllers/sessions.ts";
import {
  loadSkillsSettingsList,
  saveAllSkillParameters,
  saveSkillParameter,
  selectSkill,
  toggleSkillSettingsEnabled,
  updateSkillParamEdit,
} from "./controllers/skills-settings.ts";
import {
  installSkill,
  loadSkills,
  saveSkillApiKey,
  updateSkillCommand,
  updateSkillEdit,
  updateSkillEnabled,
} from "./controllers/skills.ts";
import { loadUsage, loadSessionTimeSeries, loadSessionLogs } from "./controllers/usage.ts";
import { icons } from "./icons.ts";
import {
  normalizeBasePath,
  TAB_GROUPS,
  subtitleForTab,
  titleForTab,
  canAccessTab,
} from "./navigation.ts";
import { renderLogin, type LoginResult } from "./views/login.ts";

// Module-scope debounce for usage date changes (avoids type-unsafe hacks on state object)
let usageDateDebounceTimeout: number | null = null;
const debouncedLoadUsage = (state: UsageState) => {
  if (usageDateDebounceTimeout) {
    clearTimeout(usageDateDebounceTimeout);
  }
  usageDateDebounceTimeout = window.setTimeout(() => void loadUsage(state), 400);
};
import {
  cancelAgentDefForm,
  createAgentDefinition,
  deleteAgentDefinition,
  loadAgentDefinitions,
  startCreateAgentDef,
  startEditAgentDef,
  toggleAgentDefinition,
  updateAgentDefForm,
  updateAgentDefinition as updateAgentDef,
  addAgentDefBinding,
  removeAgentDefBinding,
  updateAgentDefBinding,
  selectAgentDefIdentityFile,
  updateAgentDefIdentityDraft,
  saveAgentDefIdentityFile,
  resetAgentDefIdentityFile,
  type AgentDefinitionEntry,
  type AgentDefinitionsState,
} from "./controllers/agent-definitions.ts";
import {
  buildChannelConfigsProps,
  type ChannelConfigsState,
} from "./controllers/channel-configs.ts";
import {
  loadComputerStatus,
  loadComputerConfig,
  takeScreenshot,
  type ComputerState,
} from "./controllers/computer.ts";
import { buildDocumentsProps, type DocumentsState } from "./controllers/documents.ts";
import {
  buildAvatarProps,
  buildCompanyProps,
  buildEmailProps,
  buildProjectsProps,
  buildMcpSkillsProps,
  buildTasksProps,
  buildUsersProps,
  loadIgloobotTab,
  loadAgentTasks,
  loadAgentProjectCronJobs,
  loadGlobalProjectCronJobs,
  globalToggleCronJobActive,
  globalDeleteCronJob,
  globalStartCreateCronJob,
  globalCancelCreateCronJob,
  globalUpdateCronCreateForm,
  globalSaveNewCronJob,
  globalStartEditCronJob,
  globalCancelEditCronJob,
  globalUpdateCronEditForm,
  globalSaveEditedCronJob,
  switchActiveCompany,
  createNewCompany,
  loadCompanies,
  togglePinnedProject,
  pinProject,
  unpinProject,
  renameSession,
  archiveSession,
  createPinnedProjectSession,
  type AgentTasksState,
  type AgentProjectCronState,
  type IgloobotState,
  type PinState,
  type ProjectSessionEntry,
} from "./controllers/igloobot.ts";
import { sessionDisplayLabel } from "./pinned-projects.ts";
import { buildModelsProps, type ModelsState } from "./controllers/models.ts";
import { buildN8nAdminProps, type N8nState } from "./controllers/n8n.ts";
import { buildPipelinesProps, type PipelinesState } from "./controllers/pipelines.ts";
import { renderAgents } from "./views/agents.ts";
import { renderAvatarAdmin } from "./views/avatar-admin.ts";
import { renderCanvasView } from "./views/canvas.ts";
import { renderChannelConfigs } from "./views/channel-configs-admin.ts";
import { renderChannels } from "./views/channels.ts";
import { renderAgentPlans } from "./views/agent-plans.ts";
import { renderChat } from "./views/chat.ts";
import { renderCompany } from "./views/company-admin.ts";
import { renderComputerAdmin } from "./views/computer-admin.ts";
import { renderConfig } from "./views/config.ts";
import { renderCron } from "./views/cron.ts";
import { renderDebug } from "./views/debug.ts";
import { renderDocumentsAdmin } from "./views/documents-admin.ts";
import { renderEmail } from "./views/email-admin.ts";
import { renderExecApprovalPrompt } from "./views/exec-approval.ts";
import { renderGatewayUrlConfirmation } from "./views/gateway-url-confirmation.ts";
import { renderInstances } from "./views/instances.ts";

import { renderLogs } from "./views/logs.ts";
import { renderModelsAdmin } from "./views/models-admin.ts";
import { renderN8nAdmin } from "./views/n8n-admin.ts";
import { renderNodes } from "./views/nodes.ts";
import { renderOverview } from "./views/overview.ts";
import { renderPipelinesAdmin } from "./views/pipelines-admin.ts";
import { renderProjects } from "./views/projects.ts";
import { renderSecurityAdmin } from "./views/security-admin.ts";
import { renderSessions } from "./views/sessions.ts";
import { renderSkillsAdmin } from "./views/skills-admin.ts";
import { renderSkillsSettings } from "./views/skills-settings.ts";
import { renderSkills } from "./views/skills.ts";
import { renderSystemOverview } from "./views/system-overview.ts";
import { renderTasksAdmin } from "./views/tasks-admin.ts";
import { renderInferenceConfig } from "./views/inference-config.ts";
import { renderUserSettings } from "./views/user-settings.ts";
import { renderUsage } from "./views/usage.ts";
import { renderUsersAdmin } from "./views/users-admin.ts";

const AVATAR_DATA_RE = /^data:/i;
const AVATAR_HTTP_RE = /^https?:\/\//i;

function resolveFileType(filePath: string): "pdf" | "image" | "markdown" | "text" | "other" {
  const ext = filePath.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return "image";
  if (ext === "md") return "markdown";
  if (["txt", "json", "csv", "html", "htm", "js", "ts", "css", "xml", "yml", "yaml"].includes(ext)) return "text";
  return "other";
}

/** Resolve the project name for the chat header from chatContextKey. */
function resolveChatProjectName(state: AppViewState, ctxKey: string): string | null {
  if (ctxKey.startsWith("project:")) {
    const projectId = ctxKey.slice("project:".length);
    const igState = state as unknown as { igProjects?: Array<{ id: string; name: string }> };
    const project = igState.igProjects?.find((p) => p.id === projectId);
    return project?.name ?? null;
  }
  // Fallback: user's default project (named after username)
  const username = (state as unknown as { currentUser?: { username?: string } }).currentUser?.username;
  if (username) {
    const igState = state as unknown as { igProjects?: Array<{ id: string; name: string }> };
    const defaultProject = igState.igProjects?.find((p) => p.name === username);
    return defaultProject?.name ?? null;
  }
  return null;
}

/** Sections shown in the Settings tab (user-facing / daily operation). */
const SETTINGS_SECTIONS = new Set<string>([]);

/**
 * Sections excluded from the Config tab.
 * "skills" is managed via the Skills tab.
 */
const CONFIG_EXCLUDED_SECTIONS = new Set(["skills"]);

// --------------- Nav resize drag handle ---------------

const NAV_MIN_WIDTH = 180;
const NAV_MAX_WIDTH = 520;

/**
 * Attaches mousemove/mouseup listeners to window to handle a nav resize drag.
 * Updates `--nav-width` directly on the shell element for smooth 60fps feedback,
 * then persists the final width via `applySettings` on mouseup.
 */
function startNavResize(
  e: MouseEvent,
  shellEl: HTMLElement,
  startWidth: number,
  state: AppViewState,
): void {
  const startX = e.clientX;
  let currentWidth = startWidth;

  // Prevent text selection while dragging
  document.body.style.userSelect = "none";

  // Mark handle as dragging (for CSS highlight)
  const handle = (e.target as HTMLElement);
  handle.classList.add("nav-resize-handle--dragging");

  const onMouseMove = (mv: MouseEvent) => {
    const delta = mv.clientX - startX;
    const newWidth = Math.min(NAV_MAX_WIDTH, Math.max(NAV_MIN_WIDTH, startWidth + delta));
    currentWidth = newWidth;
    shellEl.style.setProperty("--nav-width", `${newWidth}px`);
  };

  const onMouseUp = () => {
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
    document.body.style.userSelect = "";
    handle.classList.remove("nav-resize-handle--dragging");

    // Persist the final width
    state.applySettings({ ...state.settings, navWidth: currentWidth });
  };

  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
}

// --------------- Pinned Projects Sidebar Section ---------------

/**
 * Renders the "Pinned projects" collapsible nav-group section.
 * Placed between the TAB_GROUPS block and the Resources block in the <aside class="nav">.
 */
function renderPinnedProjectsSection(state: AppViewState) {
  const pinState = state as unknown as PinState;
  const pinnedIds: string[] = state.settings.pinnedProjectIds ?? [];
  const allProjects = (state as unknown as IgloobotState).igProjects ?? [];
  const pinnedProjects = allProjects.filter((p) => pinnedIds.includes(p.id));
  const unpinnedProjects = allProjects.filter((p) => !pinnedIds.includes(p.id));
  const switchSession = makeSwitchSession(state);

  // Editing state is tracked per-session via lightweight module-scope maps
  // (Lit re-renders on state changes, so we hang transient input values off the DOM
  //  and read them at commit time — no need to store them on app state)

  const renderSessionRow = (projectId: string, s: ProjectSessionEntry) => {
    const label = sessionDisplayLabel(s as Parameters<typeof sessionDisplayLabel>[0]);
    const sessionKey = (s.metadata?.sessionKey as string | undefined) ?? s.id;
    const isActive = sessionKey === (state as unknown as { sessionKey: string }).sessionKey;

    return html`
      <div class="nav-item nav-item--session pinned-session ${isActive ? "pinned-session--active" : ""}">
        ${isActive ? html`<span class="pinned-session__dot"></span>` : html`<span class="pinned-session__dot-placeholder"></span>`}
        <span
          class="nav-item__text pinned-session__label"
          title=${label}
          @click=${() => {
            switchSession(sessionKey);
            state.applySettings({ ...state.settings, chatContextKey: `project:${projectId}` });
            state.setTab("chat");
          }}
        >${label}</span>
        <input
          type="text"
          class="pinned-session-rename pinned-session__rename"
          placeholder="Rename…"
          data-session-id=${s.id}
          data-project-id=${projectId}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") {
              const input = e.target as HTMLInputElement;
              const val = input.value.trim();
              if (val) void renameSession(pinState, projectId, s.id, val);
              input.value = "";
              input.style.display = "none";
            } else if (e.key === "Escape") {
              const input = e.target as HTMLInputElement;
              input.value = "";
              input.style.display = "none";
            }
          }}
        />
        <button
          class="btn btn--icon pinned-session__action"
          title="Rename session"
          @click=${(e: MouseEvent) => {
            const row = (e.target as HTMLElement).closest(".nav-item--session");
            const input = row?.querySelector<HTMLInputElement>(".pinned-session-rename");
            if (input) {
              input.style.display = input.style.display === "none" ? "inline-block" : "none";
              if (input.style.display !== "none") input.focus();
            }
          }}
        >✎</button>
        <button
          class="btn btn--icon pinned-session__action"
          title="Archive session"
          @click=${() => {
            if (window.confirm(`Archive session "${label}"? It won't be deleted and can be recovered.`)) {
              void archiveSession(pinState, projectId, s.id);
            }
          }}
        >✕</button>
      </div>
    `;
  };

  const renderPinnedProject = (project: (typeof allProjects)[0]) => {
    const id = project.id;
    const isExpanded = !!(state as unknown as { pinnedProjectsExpanded: Record<string, boolean> }).pinnedProjectsExpanded[id];
    const sessions: ProjectSessionEntry[] = (state as unknown as { pinnedProjectSessions: Record<string, ProjectSessionEntry[]> }).pinnedProjectSessions[id] ?? [];
    const loading = !!(state as unknown as { pinnedProjectSessionsLoading: Record<string, boolean> }).pinnedProjectSessionsLoading[id];

    return html`
      <div class="nav-item pinned-project">
        <button
          class="btn btn--icon pinned-project__toggle"
          title=${isExpanded ? "Collapse" : "Expand sessions"}
          @click=${() => togglePinnedProject(pinState, id)}
        >${isExpanded ? "−" : "+"}</button>
        <span
          class="pinned-project__name"
          title=${project.name}
          @click=${() => togglePinnedProject(pinState, id)}
        >${project.name}</span>
        <button
          class="btn btn--icon pinned-project__unpin"
          title="Unpin project"
          @click=${() => unpinProject(pinState, id)}
        >✕</button>
      </div>
      ${isExpanded ? html`
        <div class="pinned-project__sessions">
          ${loading
            ? html`<div class="nav-item pinned-project__sessions-loading">Loading…</div>`
            : sessions.length === 0
              ? html`<div class="nav-item pinned-project__sessions-empty">No sessions</div>`
              : repeat(sessions, (s) => s.id, (s) => renderSessionRow(id, s))
          }
          <div class="nav-item pinned-session pinned-project__new-session">
            <button
              class="btn btn--icon pinned-session__action"
              title="New session for this project"
              style="font-size:0.78em;opacity:0.7;padding:0 4px;"
              @click=${async () => {
                const sk = await createPinnedProjectSession(pinState, id);
                if (sk) {
                  switchSession(sk);
                  state.setTab("chat");
                }
              }}
            >+ New session</button>
          </div>
        </div>
      ` : nothing}
    `;
  };

  return html`
    <style>
      .pinned-project {
        padding-left: 0.5rem;
        align-items: center;
        display: flex;
        gap: 4px;
        min-height: 30px;
      }
      .pinned-project__toggle {
        padding: 0 3px;
        font-size: 0.8em;
        opacity: 0.7;
        line-height: 1;
        min-width: 16px;
      }
      .pinned-project__name {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 0.85em;
        cursor: pointer;
      }
      .pinned-project__unpin {
        padding: 0 3px;
        font-size: 0.75em;
        opacity: 0.5;
        line-height: 1;
      }
      .pinned-project__sessions {
        padding-bottom: 2px;
      }
      .pinned-project__sessions-loading {
        padding-left: 1.5rem;
        font-size: 0.8em;
        opacity: 0.5;
      }
      .pinned-project__sessions-empty {
        padding-left: 1.5rem;
        font-size: 0.8em;
        opacity: 0.4;
      }
      .pinned-session {
        padding-left: 1.5rem;
        align-items: center;
        display: flex;
        gap: 4px;
        min-height: 28px;
      }
      .pinned-session--active {
        font-weight: 600;
      }
      .pinned-session__dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--accent, #4f46e5);
        flex-shrink: 0;
      }
      .pinned-session__dot-placeholder {
        width: 6px;
        height: 6px;
        flex-shrink: 0;
      }
      .pinned-session__label {
        flex: 1;
        cursor: pointer;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 0.82em;
      }
      .pinned-session__rename {
        width: 80px;
        font-size: 0.78em;
        padding: 1px 4px;
        border: 1px solid var(--color-border, #444);
        border-radius: 3px;
        background: #fff;
        color: #000;
        display: none;
      }
      .pinned-session__action {
        padding: 0 3px;
        font-size: 0.75em;
        opacity: 0.6;
        line-height: 1;
      }
      .pinned-projects__empty {
        opacity: 0.4;
        font-size: 0.82em;
        padding-left: 0.5rem;
      }
      .pinned-projects__pin-row {
        padding-left: 0.5rem;
        align-items: center;
        display: flex;
        gap: 4px;
        min-height: 28px;
        margin-top: 2px;
      }
      .pinned-projects__pin-label {
        font-size: 0.78em;
        opacity: 0.6;
        white-space: nowrap;
      }
      .pinned-projects__pin-select {
        font-size: 0.78em;
        flex: 1;
        background: #fff;
        color: #000;
        border: 1px solid var(--color-border, #444);
        border-radius: 3px;
        padding: 1px 2px;
      }
    </style>
    <div class="nav-group">
      <button
        class="nav-label"
        @click=${() => {
          const next = { ...state.settings.navGroupsCollapsed };
          next["Pinned projects"] = !next["Pinned projects"];
          state.applySettings({ ...state.settings, navGroupsCollapsed: next });
        }}
        aria-expanded=${!(state.settings.navGroupsCollapsed["Pinned projects"] ?? false)}
      >
        <span class="nav-label__text">Pinned projects</span>
        <span class="nav-label__chevron">${(state.settings.navGroupsCollapsed["Pinned projects"] ?? false) ? "+" : "−"}</span>
      </button>
      ${(state.settings.navGroupsCollapsed["Pinned projects"] ?? false) ? nothing : html`
        <div class="nav-group__items">
          ${pinnedProjects.length === 0
            ? html`<div class="nav-item pinned-projects__empty">No pinned projects</div>`
            : pinnedProjects.map((p) => renderPinnedProject(p))
          }
          ${unpinnedProjects.length > 0 ? html`
            <div class="nav-item pinned-projects__pin-row">
              <label class="pinned-projects__pin-label">+ pin project</label>
              <select
                class="pinned-projects__pin-select"
                @change=${(e: Event) => {
                  const sel = e.target as HTMLSelectElement;
                  const val = sel.value;
                  if (val) {
                    pinProject(pinState, val);
                    sel.value = "";
                  }
                }}
              >
                <option value="">Select…</option>
                ${unpinnedProjects.map((p) => html`<option value=${p.id}>${p.name}</option>`)}
              </select>
            </div>
          ` : nothing}
        </div>
      `}
    </div>
  `;
}

function resolveAssistantAvatarUrl(state: AppViewState): string | undefined {
  const list = state.agentsList?.agents ?? [];
  const parsed = parseAgentSessionKey(state.sessionKey);
  const agentId = parsed?.agentId ?? state.agentsList?.defaultId ?? "main";
  const agent = list.find((entry) => entry.id === agentId);
  const identity = agent?.identity;
  const candidate = identity?.avatarUrl ?? identity?.avatar;
  if (!candidate) {
    return undefined;
  }
  if (AVATAR_DATA_RE.test(candidate) || AVATAR_HTTP_RE.test(candidate)) {
    return candidate;
  }
  return identity?.avatarUrl;
}

/**
 * Check if an agentId belongs to a dynamic (DB-backed) agent definition.
 */
function isDynamicAgent(state: AppViewState, agentId: string): boolean {
  return state.agentDefs.some((d) => d.agentId === agentId);
}

/**
 * Update a single field on a dynamic agent definition via RPC, then refresh.
 */
async function updateDynamicAgentField(
  state: AppViewState,
  agentId: string,
  field: string,
  value: unknown,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("igloobot.agents.update", { agentId, [field]: value });
    await loadAgentDefinitions(state as unknown as AgentDefinitionsState);
  } catch (err) {
    console.error(`[dynamic-agent] Failed to update ${field} for ${agentId}:`, err);
    state.agentsError = `Failed to update ${field}: ${String(err)}`;
  }
}

export function renderApp(state: AppViewState) {
  // ---- Login gate ----
  if (!state.isAuthenticated) {
    return renderLogin(
      {
        client: state.client,
        connected: state.connected,
        lastError: state.lastError,
        basePath: state.basePath ?? "",
      },
      (result: LoginResult) => {
        state.handleLogin({
          user: result.user as Parameters<typeof state.handleLogin>[0]["user"],
          token: result.token,
        });
      },
    );
  }

  const presenceCount = state.presenceEntries.length;
  const sessionsCount = state.sessionsResult?.count ?? null;
  const cronNext = state.cronStatus?.nextWakeAtMs ?? null;
  const chatDisabledReason = state.connected ? null : "Disconnected from gateway.";
  const isChat = state.tab === "chat";
  const chatFocus = isChat && (state.settings.chatFocusMode || state.onboarding);
  const showThinking = state.onboarding ? false : state.settings.chatShowThinking;
  const assistantAvatarUrl = resolveAssistantAvatarUrl(state);
  const chatAvatarUrl = state.chatAvatarUrl ?? assistantAvatarUrl ?? null;
  const configValue =
    state.configForm ?? (state.configSnapshot?.config as Record<string, unknown> | null);
  const basePath = normalizeBasePath(state.basePath ?? "");
  const resolvedAgentId =
    state.agentsSelectedId ??
    state.agentsList?.defaultId ??
    state.agentsList?.agents?.[0]?.id ??
    null;
  const userRole = state.currentUser?.globalRole ?? null;
  const navWidth = state.settings.navWidth;

  return html`
    <div
      class="shell ${isChat ? "shell--chat" : ""} ${chatFocus ? "shell--chat-focus" : ""} ${state.settings.navCollapsed ? "shell--nav-collapsed" : ""} ${state.onboarding ? "shell--onboarding" : ""}"
      style=${navWidth != null && !state.settings.navCollapsed ? `--nav-width:${navWidth}px` : ""}
    >
      <header class="topbar">
        <div class="topbar-left">
          <button
            class="nav-collapse-toggle"
            @click=${() =>
              state.applySettings({
                ...state.settings,
                navCollapsed: !state.settings.navCollapsed,
              })}
            title="${state.settings.navCollapsed ? "Expand sidebar" : "Collapse sidebar"}"
            aria-label="${state.settings.navCollapsed ? "Expand sidebar" : "Collapse sidebar"}"
          >
            <span class="nav-collapse-toggle__icon">${icons.menu}</span>
          </button>
          <div class="brand">
            <div class="brand-logo">
              <img src=${basePath ? `${basePath}/icon-512.png` : "/icon-512.png"} alt="" role="presentation" />
            </div>
            <div class="brand-text">
              <div class="brand-title">i-GlooBot</div>
              <div class="brand-sub">Agent Dashboard</div>
            </div>
          </div>
        </div>
        ${
          state.igCompanies.length > 0
            ? html`
          <div class="topbar-company-picker">
            <select
              class="company-select"
              .value=${state.igActiveCompanyId ?? ""}
              @change=${(e: Event) => {
                const id = (e.target as HTMLSelectElement).value;
                if (id) {
                  void switchActiveCompany(state as unknown as IgloobotState, id);
                }
              }}
              title="Switch active company"
            >
              ${state.igCompanies.map(
                (c) => html`
                  <option value=${c.id} ?selected=${c.id === state.igActiveCompanyId}>
                    ${c.name || "(Unnamed Company)"}
                  </option>
                `,
              )}
            </select>
            <button
              class="company-add-btn"
              @click=${() => void createNewCompany(state as unknown as IgloobotState)}
              title="Add new company"
              aria-label="Add new company"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" style="stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round"><line x1="12" x2="12" y1="5" y2="19"/><line x1="5" x2="19" y1="12" y2="12"/></svg>
            </button>
          </div>
        `
            : nothing
        }
        <div class="topbar-status">
          <div class="pill">
            <span class="statusDot ${state.connected ? "ok" : ""}"></span>
            <span>Health</span>
            <span class="mono">${state.connected ? "OK" : "Offline"}</span>
          </div>
          ${renderThemeToggle(state)}
          ${
            state.currentUser
              ? html`
                <div class="pill pill--user" title="${state.currentUser.username} (${state.currentUser.globalRole})">
                  <span class="mono">${state.currentUser.username}</span>
                  <button
                    class="logout-btn"
                    @click=${() => state.handleLogout()}
                    title="Sign out"
                    aria-label="Sign out"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" style="stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
                  </button>
                </div>
              `
              : nothing
          }
        </div>
      </header>
      <aside class="nav ${state.settings.navCollapsed ? "nav--collapsed" : ""}">
        ${!state.settings.navCollapsed ? html`
          <div class="nav-tabs">
            ${(["coworker", "notebook", "legal", "crm"] as const).map((mode) => {
              const isActive = (state.settings.sidebarMode ?? "coworker") === mode;
              const label = mode === "crm" ? "i-GlooCRM" : mode.charAt(0).toUpperCase() + mode.slice(1);
              return html`<button
                class="nav-tab ${isActive ? "nav-tab--active" : ""}"
                @click=${() => state.applySettings({ ...state.settings, sidebarMode: mode })}
              >${label}</button>`;
            })}
          </div>
        ` : nothing}
        ${(state.settings.sidebarMode ?? "coworker") === "coworker"
          ? html`
            ${TAB_GROUPS.map((group) => {
          const visibleTabs = group.tabs.filter((tab) =>
            canAccessTab(tab, userRole, state.tabVisibilityMap),
          );
          if (visibleTabs.length === 0) {
            return nothing;
          }
          const isGroupCollapsed = state.settings.navGroupsCollapsed[group.label] ?? false;
          const hasActiveTab = visibleTabs.some((tab) => tab === state.tab);
          const isChatGroup = (group.tabs as readonly string[]).includes("chat");
          return html`
            <div class="nav-group ${isGroupCollapsed && !hasActiveTab ? "nav-group--collapsed" : ""}">
              ${
                group.label
                  ? html`<button
                    class="nav-label"
                    @click=${() => {
                      const next = { ...state.settings.navGroupsCollapsed };
                      next[group.label] = !isGroupCollapsed;
                      state.applySettings({
                        ...state.settings,
                        navGroupsCollapsed: next,
                      });
                    }}
                    aria-expanded=${!isGroupCollapsed}
                  >
                    <span class="nav-label__text">${group.label}</span>
                    <span class="nav-label__chevron">${isGroupCollapsed ? "+" : "−"}</span>
                  </button>`
                  : nothing
              }
              <div class="nav-group__items">
                ${visibleTabs.map((tab) => renderTab(state, tab))}
              </div>
            </div>
            ${isChatGroup ? renderPinnedProjectsSection(state) : nothing}
          `;
        })}

        <div class="nav-group nav-group--links">
          <div class="nav-label nav-label--static">
            <span class="nav-label__text">Resources</span>
          </div>
          <div class="nav-group__items">
            <span
              class="nav-item nav-item--external"
              style="opacity: 0.5; cursor: default"
              title="Documentation coming soon"
            >
              <span class="nav-item__icon" aria-hidden="true">${icons.book}</span>
              <span class="nav-item__text">Docs</span>
            </span>
          </div>
        </div>
        `
          : html`<div class="nav-coming-soon">
              <div class="nav-coming-soon__icon">📋</div>
              <div class="nav-coming-soon__title">${
                (state.settings.sidebarMode ?? "coworker") === "notebook"
                  ? "Notebook"
                  : (state.settings.sidebarMode ?? "coworker") === "crm"
                    ? "i-GlooCRM"
                    : "Legal"
              }</div>
              <div class="nav-coming-soon__text">Coming soon</div>
            </div>`
        }
        ${state.settings.navCollapsed ? nothing : html`
          <div
            class="nav-resize-handle"
            aria-hidden="true"
            @mousedown=${(e: MouseEvent) => {
              const shellEl = (e.target as HTMLElement).closest<HTMLElement>(".shell");
              if (!shellEl) return;
              // Read current --nav-width from the shell, fall back to CSS default 220px
              const computedWidth = parseInt(
                getComputedStyle(shellEl).getPropertyValue("--nav-width").trim() || "220",
                10,
              );
              const startWidth = isNaN(computedWidth) ? 220 : computedWidth;
              startNavResize(e, shellEl, startWidth, state);
            }}
          ></div>
        `}
      </aside>
      <main class="content ${isChat ? "content--chat" : ""}">
        <section class="content-header">
          <div>
            ${
              state.tab === "usage"
                ? nothing
                : html`<div class="page-title">${
                    isChat
                      ? (() => {
                          const ctxKey = state.settings.chatContextKey || "";
                          const projectName = resolveChatProjectName(state, ctxKey);
                          return projectName ? `Chat — ${projectName}` : "Chat";
                        })()
                      : titleForTab(state.tab)
                  }</div>`
            }
            ${state.tab === "usage" || !subtitleForTab(state.tab) ? nothing : html`<div class="page-sub">${subtitleForTab(state.tab)}</div>`}
          </div>
          <div class="page-meta">
            ${state.lastError ? html`<div class="pill danger">${state.lastError}</div>` : nothing}
            ${isChat ? renderChatControls(state) : nothing}
          </div>
        </section>

        ${
          state.tab === "overview"
            ? renderOverview({
                ...state.overviewData,
                connected: state.connected,
                hello: state.hello,
                settings: state.settings,
                password: state.password,
                lastError: state.lastError,
                presenceCount,
                sessionsCount,
                cronEnabled: state.cronStatus?.enabled ?? null,
                cronNext,
                lastChannelsRefresh: state.channelsLastSuccess,
                igStats: state.igStats,
                igStatsAvailable: state.igStatsAvailable,
                onSettingsChange: (next) => state.applySettings(next),
                onPasswordChange: (next) => (state.password = next),
                onSessionKeyChange: (next) => {
                  state.sessionKey = next;
                  state.chatMessage = "";
                  state.resetToolStream();
                  state.applySettings({
                    ...state.settings,
                    sessionKey: next,
                    lastActiveSessionKey: next,
                  });
                  void state.loadAssistantIdentity();
                },
                onConnect: () => state.connect(),
                onRefresh: () => state.loadOverview(),
                onTimeRangeChange: (range) => {
                  state.overviewData.overviewTimeRange = range;
                  void state.loadOverview();
                },
                presenceEntries: state.presenceEntries,
                presenceLoading: state.presenceLoading,
                presenceError: state.presenceError,
                presenceStatus: state.presenceStatus,
                onRefreshPresence: () => loadPresence(state),
              })
            : nothing
        }

        ${
          state.tab === "channels"
            ? html`
                <div style="margin-bottom: 24px;">
                  ${renderChannelConfigs(buildChannelConfigsProps(state as unknown as ChannelConfigsState))}
                </div>
                ${renderChannels({
                  connected: state.connected,
                  loading: state.channelsLoading,
                  snapshot: state.channelsSnapshot,
                  lastError: state.channelsError,
                  lastSuccessAt: state.channelsLastSuccess,
                  whatsappMessage: state.whatsappLoginMessage,
                  whatsappQrDataUrl: state.whatsappLoginQrDataUrl,
                  whatsappConnected: state.whatsappLoginConnected,
                  whatsappBusy: state.whatsappBusy,
                  configSchema: state.configSchema,
                  configSchemaLoading: state.configSchemaLoading,
                  configForm: state.configForm,
                  configUiHints: state.configUiHints,
                  configSaving: state.configSaving,
                  configFormDirty: state.configFormDirty,
                  configFormKey: state.configFormKey,
                  onRefresh: (probe) => loadChannels(state, probe),
                  onWhatsAppStart: (force) => state.handleWhatsAppStart(force),
                  onWhatsAppWait: () => state.handleWhatsAppWait(),
                  onWhatsAppLogout: () => state.handleWhatsAppLogout(),
                  onConfigPatch: (path, value) => updateConfigFormValue(state, path, value),
                  onConfigSave: () => state.handleChannelConfigSave(),
                  onConfigReload: () => state.handleChannelConfigReload(),
                  emailStatus: state.igEmailStatus,
                  onManageEmail: () => state.setTab("email"),
                })}
              `
            : nothing
        }

        ${
          state.tab === "sessions"
            ? renderSessions({
                loading: state.sessionsLoading,
                result: state.sessionsResult,
                error: state.sessionsError,
                activeMinutes: state.sessionsFilterActive,
                limit: state.sessionsFilterLimit,
                includeGlobal: state.sessionsIncludeGlobal,
                includeUnknown: state.sessionsIncludeUnknown,
                basePath: state.basePath,
                onFiltersChange: (next) => {
                  state.sessionsFilterActive = next.activeMinutes;
                  state.sessionsFilterLimit = next.limit;
                  state.sessionsIncludeGlobal = next.includeGlobal;
                  state.sessionsIncludeUnknown = next.includeUnknown;
                },
                onRefresh: () => loadSessions(state),
                onPatch: (key, patch) => patchSession(state, key, patch),
                onDelete: (key) => deleteSession(state, key),
              })
            : nothing
        }

        ${
          state.tab === "usage"
            ? renderUsage({
                loading: state.usageLoading,
                error: state.usageError,
                startDate: state.usageStartDate,
                endDate: state.usageEndDate,
                sessions: state.usageResult?.sessions ?? [],
                sessionsLimitReached: (state.usageResult?.sessions?.length ?? 0) >= 1000,
                totals: state.usageResult?.totals ?? null,
                aggregates: state.usageResult?.aggregates ?? null,
                costDaily: state.usageCostSummary?.daily ?? [],
                selectedSessions: state.usageSelectedSessions,
                selectedDays: state.usageSelectedDays,
                selectedHours: state.usageSelectedHours,
                chartMode: state.usageChartMode,
                dailyChartMode: state.usageDailyChartMode,
                timeSeriesMode: state.usageTimeSeriesMode,
                timeSeriesBreakdownMode: state.usageTimeSeriesBreakdownMode,
                timeSeries: state.usageTimeSeries,
                timeSeriesLoading: state.usageTimeSeriesLoading,
                sessionLogs: state.usageSessionLogs,
                sessionLogsLoading: state.usageSessionLogsLoading,
                sessionLogsExpanded: state.usageSessionLogsExpanded,
                logFilterRoles: state.usageLogFilterRoles,
                logFilterTools: state.usageLogFilterTools,
                logFilterHasTools: state.usageLogFilterHasTools,
                logFilterQuery: state.usageLogFilterQuery,
                query: state.usageQuery,
                queryDraft: state.usageQueryDraft,
                sessionSort: state.usageSessionSort,
                sessionSortDir: state.usageSessionSortDir,
                recentSessions: state.usageRecentSessions,
                sessionsTab: state.usageSessionsTab,
                visibleColumns:
                  state.usageVisibleColumns as import("./views/usage.ts").UsageColumnId[],
                timeZone: state.usageTimeZone,
                contextExpanded: state.usageContextExpanded,
                headerPinned: state.usageHeaderPinned,
                onStartDateChange: (date) => {
                  state.usageStartDate = date;
                  state.usageSelectedDays = [];
                  state.usageSelectedHours = [];
                  state.usageSelectedSessions = [];
                  debouncedLoadUsage(state);
                },
                onEndDateChange: (date) => {
                  state.usageEndDate = date;
                  state.usageSelectedDays = [];
                  state.usageSelectedHours = [];
                  state.usageSelectedSessions = [];
                  debouncedLoadUsage(state);
                },
                onRefresh: () => loadUsage(state),
                onTimeZoneChange: (zone) => {
                  state.usageTimeZone = zone;
                },
                onToggleContextExpanded: () => {
                  state.usageContextExpanded = !state.usageContextExpanded;
                },
                onToggleSessionLogsExpanded: () => {
                  state.usageSessionLogsExpanded = !state.usageSessionLogsExpanded;
                },
                onLogFilterRolesChange: (next) => {
                  state.usageLogFilterRoles = next;
                },
                onLogFilterToolsChange: (next) => {
                  state.usageLogFilterTools = next;
                },
                onLogFilterHasToolsChange: (next) => {
                  state.usageLogFilterHasTools = next;
                },
                onLogFilterQueryChange: (next) => {
                  state.usageLogFilterQuery = next;
                },
                onLogFilterClear: () => {
                  state.usageLogFilterRoles = [];
                  state.usageLogFilterTools = [];
                  state.usageLogFilterHasTools = false;
                  state.usageLogFilterQuery = "";
                },
                onToggleHeaderPinned: () => {
                  state.usageHeaderPinned = !state.usageHeaderPinned;
                },
                onSelectHour: (hour, shiftKey) => {
                  if (shiftKey && state.usageSelectedHours.length > 0) {
                    const allHours = Array.from({ length: 24 }, (_, i) => i);
                    const lastSelected =
                      state.usageSelectedHours[state.usageSelectedHours.length - 1];
                    const lastIdx = allHours.indexOf(lastSelected);
                    const thisIdx = allHours.indexOf(hour);
                    if (lastIdx !== -1 && thisIdx !== -1) {
                      const [start, end] =
                        lastIdx < thisIdx ? [lastIdx, thisIdx] : [thisIdx, lastIdx];
                      const range = allHours.slice(start, end + 1);
                      state.usageSelectedHours = [
                        ...new Set([...state.usageSelectedHours, ...range]),
                      ];
                    }
                  } else {
                    if (state.usageSelectedHours.includes(hour)) {
                      state.usageSelectedHours = state.usageSelectedHours.filter((h) => h !== hour);
                    } else {
                      state.usageSelectedHours = [...state.usageSelectedHours, hour];
                    }
                  }
                },
                onQueryDraftChange: (query) => {
                  state.usageQueryDraft = query;
                  if (state.usageQueryDebounceTimer) {
                    window.clearTimeout(state.usageQueryDebounceTimer);
                  }
                  state.usageQueryDebounceTimer = window.setTimeout(() => {
                    state.usageQuery = state.usageQueryDraft;
                    state.usageQueryDebounceTimer = null;
                  }, 250);
                },
                onApplyQuery: () => {
                  if (state.usageQueryDebounceTimer) {
                    window.clearTimeout(state.usageQueryDebounceTimer);
                    state.usageQueryDebounceTimer = null;
                  }
                  state.usageQuery = state.usageQueryDraft;
                },
                onClearQuery: () => {
                  if (state.usageQueryDebounceTimer) {
                    window.clearTimeout(state.usageQueryDebounceTimer);
                    state.usageQueryDebounceTimer = null;
                  }
                  state.usageQueryDraft = "";
                  state.usageQuery = "";
                },
                onSessionSortChange: (sort) => {
                  state.usageSessionSort = sort;
                },
                onSessionSortDirChange: (dir) => {
                  state.usageSessionSortDir = dir;
                },
                onSessionsTabChange: (tab) => {
                  state.usageSessionsTab = tab;
                },
                onToggleColumn: (column) => {
                  if (state.usageVisibleColumns.includes(column)) {
                    state.usageVisibleColumns = state.usageVisibleColumns.filter(
                      (entry) => entry !== column,
                    );
                  } else {
                    state.usageVisibleColumns = [...state.usageVisibleColumns, column];
                  }
                },
                onSelectSession: (key, shiftKey) => {
                  state.usageTimeSeries = null;
                  state.usageSessionLogs = null;
                  state.usageRecentSessions = [
                    key,
                    ...state.usageRecentSessions.filter((entry) => entry !== key),
                  ].slice(0, 8);

                  if (shiftKey && state.usageSelectedSessions.length > 0) {
                    // Shift-click: select range from last selected to this session
                    // Sort sessions same way as displayed (by tokens or cost descending)
                    const isTokenMode = state.usageChartMode === "tokens";
                    const sortedSessions = [...(state.usageResult?.sessions ?? [])].toSorted(
                      (a, b) => {
                        const valA = isTokenMode
                          ? (a.usage?.totalTokens ?? 0)
                          : (a.usage?.totalCost ?? 0);
                        const valB = isTokenMode
                          ? (b.usage?.totalTokens ?? 0)
                          : (b.usage?.totalCost ?? 0);
                        return valB - valA;
                      },
                    );
                    const allKeys = sortedSessions.map((s) => s.key);
                    const lastSelected =
                      state.usageSelectedSessions[state.usageSelectedSessions.length - 1];
                    const lastIdx = allKeys.indexOf(lastSelected);
                    const thisIdx = allKeys.indexOf(key);
                    if (lastIdx !== -1 && thisIdx !== -1) {
                      const [start, end] =
                        lastIdx < thisIdx ? [lastIdx, thisIdx] : [thisIdx, lastIdx];
                      const range = allKeys.slice(start, end + 1);
                      const newSelection = [...new Set([...state.usageSelectedSessions, ...range])];
                      state.usageSelectedSessions = newSelection;
                    }
                  } else {
                    // Regular click: focus a single session (so details always open).
                    // Click the focused session again to clear selection.
                    if (
                      state.usageSelectedSessions.length === 1 &&
                      state.usageSelectedSessions[0] === key
                    ) {
                      state.usageSelectedSessions = [];
                    } else {
                      state.usageSelectedSessions = [key];
                    }
                  }

                  // Load timeseries/logs only if exactly one session selected
                  if (state.usageSelectedSessions.length === 1) {
                    void loadSessionTimeSeries(state, state.usageSelectedSessions[0]);
                    void loadSessionLogs(state, state.usageSelectedSessions[0]);
                  }
                },
                onSelectDay: (day, shiftKey) => {
                  if (shiftKey && state.usageSelectedDays.length > 0) {
                    // Shift-click: select range from last selected to this day
                    const allDays = (state.usageCostSummary?.daily ?? []).map((d) => d.date);
                    const lastSelected =
                      state.usageSelectedDays[state.usageSelectedDays.length - 1];
                    const lastIdx = allDays.indexOf(lastSelected);
                    const thisIdx = allDays.indexOf(day);
                    if (lastIdx !== -1 && thisIdx !== -1) {
                      const [start, end] =
                        lastIdx < thisIdx ? [lastIdx, thisIdx] : [thisIdx, lastIdx];
                      const range = allDays.slice(start, end + 1);
                      // Merge with existing selection
                      const newSelection = [...new Set([...state.usageSelectedDays, ...range])];
                      state.usageSelectedDays = newSelection;
                    }
                  } else {
                    // Regular click: toggle single day
                    if (state.usageSelectedDays.includes(day)) {
                      state.usageSelectedDays = state.usageSelectedDays.filter((d) => d !== day);
                    } else {
                      state.usageSelectedDays = [day];
                    }
                  }
                },
                onChartModeChange: (mode) => {
                  state.usageChartMode = mode;
                },
                onDailyChartModeChange: (mode) => {
                  state.usageDailyChartMode = mode;
                },
                onTimeSeriesModeChange: (mode) => {
                  state.usageTimeSeriesMode = mode;
                },
                onTimeSeriesBreakdownChange: (mode) => {
                  state.usageTimeSeriesBreakdownMode = mode;
                },
                onClearDays: () => {
                  state.usageSelectedDays = [];
                },
                onClearHours: () => {
                  state.usageSelectedHours = [];
                },
                onClearSessions: () => {
                  state.usageSelectedSessions = [];
                  state.usageTimeSeries = null;
                  state.usageSessionLogs = null;
                },
                onClearFilters: () => {
                  state.usageSelectedDays = [];
                  state.usageSelectedHours = [];
                  state.usageSelectedSessions = [];
                  state.usageTimeSeries = null;
                  state.usageSessionLogs = null;
                },
                usageSubTab: state.usageSubTab,
                onUsageSubTabChange: (tab) => {
                  state.usageSubTab = tab;
                  if (tab === "invoices") {
                    const companyId = state.igActiveCompanyId ?? "";
                    if (companyId) {
                      void loadBillingSummary(state, companyId);
                    }
                  }
                },
                billingInvoices: state.billingInvoices,
                billingLoading: state.billingLoading,
                billingError: state.billingError,
                onDownloadInvoice: (invoiceId) => void downloadInvoice(state, invoiceId),
              })
            : nothing
        }

        ${
          state.tab === "cron"
            ? renderCron({
                basePath: state.basePath,
                loading: state.cronLoading,
                status: state.cronStatus,
                jobs: state.cronJobs,
                error: state.cronError,
                busy: state.cronBusy,
                form: state.cronForm,
                channels: state.channelsSnapshot?.channelMeta?.length
                  ? state.channelsSnapshot.channelMeta.map((entry) => entry.id)
                  : (state.channelsSnapshot?.channelOrder ?? []),
                channelLabels: state.channelsSnapshot?.channelLabels ?? {},
                channelMeta: state.channelsSnapshot?.channelMeta ?? [],
                runsJobId: state.cronRunsJobId,
                runs: state.cronRuns,
                projectCronJobs: state.igGlobalCronJobs,
                projectCronJobsLoading: state.igGlobalCronJobsLoading,
                cronTemplates: (state as unknown as IgloobotState).igCronTemplates ?? [],
                projects: (state as unknown as IgloobotState).igProjects ?? [],
                agents: (state.agentsList?.agents ?? []).map((a) => ({
                  id: a.id,
                  name: a.name ?? a.identity?.name ?? a.id,
                })),
                projectFilter: state.igGlobalCronProjectFilter,
                agentFilter: state.igGlobalCronAgentFilter,
                onFormChange: (patch) => (state.cronForm = { ...state.cronForm, ...patch }),
                onRefresh: () => state.loadCron(),
                onAdd: () => addCronJob(state),
                onToggle: (job, enabled) => toggleCronJob(state, job, enabled),
                onRun: (job) => runCronJob(state, job),
                onRemove: (job) => removeCronJob(state, job),
                onLoadRuns: (jobId) => loadCronRuns(state, jobId),
                onFilterProject: (projectId) => {
                  state.igGlobalCronProjectFilter = projectId;
                  void loadGlobalProjectCronJobs(state as unknown as IgloobotState);
                },
                onFilterAgent: (agentId) => {
                  state.igGlobalCronAgentFilter = agentId;
                  void loadGlobalProjectCronJobs(state as unknown as IgloobotState);
                },
                // Project cron CRUD state
                projectCronCreateMode: (state as unknown as IgloobotState).igCronCreateMode,
                projectCronCreateForm: (state as unknown as IgloobotState).igCronCreateForm,
                projectCronEditingId: (state as unknown as IgloobotState).igCronEditingId,
                projectCronEditForm: (state as unknown as IgloobotState).igCronEditForm,
                // Project cron callbacks
                onProjectCronRefresh: () =>
                  void loadGlobalProjectCronJobs(state as unknown as IgloobotState),
                onProjectCronToggle: (id, active) =>
                  void globalToggleCronJobActive(state as unknown as IgloobotState, id, active),
                onProjectCronDelete: (id) =>
                  void globalDeleteCronJob(state as unknown as IgloobotState, id),
                onProjectCronCreate: () =>
                  globalStartCreateCronJob(state as unknown as IgloobotState),
                onProjectCronCancelCreate: () =>
                  globalCancelCreateCronJob(state as unknown as IgloobotState),
                onProjectCronFormChange: (field, value) =>
                  globalUpdateCronCreateForm(state as unknown as IgloobotState, field, value),
                onProjectCronSave: () =>
                  void globalSaveNewCronJob(state as unknown as IgloobotState),
                onProjectCronEdit: (job) =>
                  globalStartEditCronJob(state as unknown as IgloobotState, job),
                onProjectCronCancelEdit: () =>
                  globalCancelEditCronJob(state as unknown as IgloobotState),
                onProjectCronEditFormChange: (field, value) =>
                  globalUpdateCronEditForm(state as unknown as IgloobotState, field, value),
                onProjectCronSaveEdit: () =>
                  void globalSaveEditedCronJob(state as unknown as IgloobotState),
              })
            : nothing
        }

        ${
          state.tab === "agents"
            ? renderAgents({
                loading: state.agentsLoading,
                error: state.agentsError,
                agentsList: state.agentsList,
                selectedAgentId: resolvedAgentId,
                activePanel: state.agentsPanel,
                configForm: configValue,
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                modelCatalog: state.modelCatalog,
                channelsLoading: state.channelsLoading,
                channelsError: state.channelsError,
                channelsSnapshot: state.channelsSnapshot,
                channelsLastSuccess: state.channelsLastSuccess,
                cronLoading: state.cronLoading,
                cronStatus: state.cronStatus,
                cronJobs: state.cronJobs,
                cronError: state.cronError,
                agentFilesLoading: state.agentFilesLoading,
                agentFilesError: state.agentFilesError,
                agentFilesList: state.agentFilesList,
                agentFileActive: state.agentFileActive,
                agentFileContents: state.agentFileContents,
                agentFileDrafts: state.agentFileDrafts,
                agentFileSaving: state.agentFileSaving,
                agentIdentityLoading: state.agentIdentityLoading,
                agentIdentityError: state.agentIdentityError,
                agentIdentityById: state.agentIdentityById,
                agentSkillsLoading: state.agentSkillsLoading,
                agentSkillsReport: state.agentSkillsReport,
                agentSkillsError: state.agentSkillsError,
                agentSkillsAgentId: state.agentSkillsAgentId,
                skillsFilter: state.skillsFilter,
                agentTasksLoading: state.agentTasksLoading,
                agentTasksError: state.agentTasksError,
                agentTasks: state.agentTasks,
                onAgentTasksRefresh: () => {
                  if (resolvedAgentId) {
                    void loadAgentTasks(state as unknown as AgentTasksState, resolvedAgentId);
                  }
                },
                agentProjectCronJobs: state.agentProjectCronJobs,
                agentProjectCronJobsLoading: state.agentProjectCronJobsLoading,
                agentDefs: state.agentDefs,
                agentDefsLoading: state.agentDefsLoading,
                agentDefsError: state.agentDefsError,
                agentDefCreateMode: state.agentDefCreateMode,
                agentDefEditingId: state.agentDefEditingId,
                agentDefForm: state.agentDefForm,
                projects: (state as unknown as N8nState).igProjects ?? [],
                onRefresh: async () => {
                  await loadAgents(state);
                  void loadAgentDefinitions(state as unknown as AgentDefinitionsState);
                  const agentIds = state.agentsList?.agents?.map((entry) => entry.id) ?? [];
                  if (agentIds.length > 0) {
                    void loadAgentIdentities(state, agentIds);
                  }
                },
                onSelectAgent: (agentId) => {
                  if (state.agentsSelectedId === agentId) {
                    return;
                  }
                  // Clear any in-progress dynamic agent create/edit form
                  cancelAgentDefForm(state as unknown as AgentDefinitionsState);
                  state.agentsSelectedId = agentId;
                  state.agentFilesList = null;
                  state.agentFilesError = null;
                  state.agentFilesLoading = false;
                  state.agentFileActive = null;
                  state.agentFileContents = {};
                  state.agentFileDrafts = {};
                  state.agentSkillsReport = null;
                  state.agentSkillsError = null;
                  state.agentSkillsAgentId = null;
                  state.agentTasks = [];
                  state.agentTasksError = null;
                  state.agentProjectCronJobs = [];
                  void loadAgentIdentity(state, agentId);
                  if (state.agentsPanel === "files") {
                    void loadAgentFiles(state, agentId);
                  }
                  if (state.agentsPanel === "skills") {
                    void loadAgentSkills(state, agentId);
                  }
                  if (state.agentsPanel === "tasks") {
                    void loadAgentTasks(state as unknown as AgentTasksState, agentId);
                  }
                  if (state.agentsPanel === "cron") {
                    void loadAgentProjectCronJobs(
                      state as unknown as AgentProjectCronState,
                      agentId,
                    );
                  }
                },
                onSelectPanel: (panel) => {
                  state.agentsPanel = panel;
                  if (panel === "files" && resolvedAgentId) {
                    // Always reload files on tab switch to avoid stale state
                    // from previous sessions or gateway reconnects.
                    state.agentFilesList = null;
                    state.agentFilesError = null;
                    state.agentFileActive = null;
                    state.agentFileContents = {};
                    state.agentFileDrafts = {};
                    void loadAgentFiles(state, resolvedAgentId);
                  }
                  if (panel === "skills") {
                    if (resolvedAgentId) {
                      void loadAgentSkills(state, resolvedAgentId);
                    }
                  }
                  if (panel === "tasks") {
                    if (resolvedAgentId) {
                      void loadAgentTasks(state as unknown as AgentTasksState, resolvedAgentId);
                    }
                  }
                  if (panel === "channels") {
                    void loadChannels(state, false);
                  }
                  if (panel === "cron") {
                    void state.loadCron();
                    if (resolvedAgentId) {
                      void loadAgentProjectCronJobs(
                        state as unknown as AgentProjectCronState,
                        resolvedAgentId,
                      );
                    }
                  }
                },
                onLoadFiles: (agentId) => {
                  // Reset loading state in case it's stuck from a previous failed attempt
                  state.agentFilesLoading = false;
                  state.agentFilesError = null;
                  void loadAgentFiles(state, agentId);
                },
                onSelectFile: (name) => {
                  state.agentFileActive = name;
                  if (!resolvedAgentId) {
                    return;
                  }
                  void loadAgentFileContent(state, resolvedAgentId, name);
                },
                onFileDraftChange: (name, content) => {
                  state.agentFileDrafts = { ...state.agentFileDrafts, [name]: content };
                },
                onFileReset: (name) => {
                  const base = state.agentFileContents[name] ?? "";
                  state.agentFileDrafts = { ...state.agentFileDrafts, [name]: base };
                },
                onFileSave: (name) => {
                  if (!resolvedAgentId) {
                    return;
                  }
                  const content =
                    state.agentFileDrafts[name] ?? state.agentFileContents[name] ?? "";
                  void saveAgentFile(state, resolvedAgentId, name, content);
                },
                onToolsProfileChange: (agentId, profile, clearAllow) => {
                  // Dynamic agents: update toolPolicy in DB definition
                  if (isDynamicAgent(state, agentId)) {
                    const def = state.agentDefs.find((d) => d.agentId === agentId);
                    const current = (def?.toolPolicy ?? {}) as Record<string, unknown>;
                    const next = { ...current };
                    if (profile) {
                      next.profile = profile;
                    } else {
                      delete next.profile;
                    }
                    if (clearAllow) {
                      delete next.allow;
                    }
                    void updateDynamicAgentField(state, agentId, "toolPolicy", next);
                    return;
                  }
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "tools"];
                  if (profile) {
                    updateConfigFormValue(state, [...basePath, "profile"], profile);
                  } else {
                    removeConfigFormValue(state, [...basePath, "profile"]);
                  }
                  if (clearAllow) {
                    removeConfigFormValue(state, [...basePath, "allow"]);
                  }
                },
                onToolsOverridesChange: (agentId, alsoAllow, deny) => {
                  // Dynamic agents: update toolPolicy in DB definition
                  if (isDynamicAgent(state, agentId)) {
                    const def = state.agentDefs.find((d) => d.agentId === agentId);
                    const current = (def?.toolPolicy ?? {}) as Record<string, unknown>;
                    const next = { ...current };
                    if (alsoAllow.length > 0) {
                      next.alsoAllow = alsoAllow;
                    } else {
                      delete next.alsoAllow;
                    }
                    if (deny.length > 0) {
                      next.deny = deny;
                    } else {
                      delete next.deny;
                    }
                    void updateDynamicAgentField(state, agentId, "toolPolicy", next);
                    return;
                  }
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "tools"];
                  if (alsoAllow.length > 0) {
                    updateConfigFormValue(state, [...basePath, "alsoAllow"], alsoAllow);
                  } else {
                    removeConfigFormValue(state, [...basePath, "alsoAllow"]);
                  }
                  if (deny.length > 0) {
                    updateConfigFormValue(state, [...basePath, "deny"], deny);
                  } else {
                    removeConfigFormValue(state, [...basePath, "deny"]);
                  }
                },
                onConfigReload: () => loadConfig(state),
                onConfigSave: () => saveConfig(state),
                onChannelsRefresh: () => loadChannels(state, false),
                onCronRefresh: () => state.loadCron(),
                onSkillsFilterChange: (next) => (state.skillsFilter = next),
                onSkillsRefresh: () => {
                  if (resolvedAgentId) {
                    void loadAgentSkills(state, resolvedAgentId);
                  }
                },
                onAgentSkillToggle: (agentId, skillName, enabled) => {
                  const normalizedSkill = skillName.trim();
                  if (!normalizedSkill) {
                    return;
                  }
                  // Dynamic agents: update skills array in DB definition
                  if (isDynamicAgent(state, agentId)) {
                    const def = state.agentDefs.find((d) => d.agentId === agentId);
                    const allSkills =
                      state.agentSkillsReport?.skills?.map((s) => s.name).filter(Boolean) ?? [];
                    const existing =
                      Array.isArray(def?.skills) && def.skills.length > 0
                        ? [...def.skills]
                        : undefined;
                    const base = existing ?? allSkills;
                    const next = new Set(base);
                    if (enabled) {
                      next.add(normalizedSkill);
                    } else {
                      next.delete(normalizedSkill);
                    }
                    void updateDynamicAgentField(state, agentId, "skills", [...next]);
                    return;
                  }
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const entry = list[index] as { skills?: unknown };
                  const allSkills =
                    state.agentSkillsReport?.skills?.map((skill) => skill.name).filter(Boolean) ??
                    [];
                  const existing = Array.isArray(entry.skills)
                    ? entry.skills.map((name) => String(name).trim()).filter(Boolean)
                    : undefined;
                  const base = existing ?? allSkills;
                  const next = new Set(base);
                  if (enabled) {
                    next.add(normalizedSkill);
                  } else {
                    next.delete(normalizedSkill);
                  }
                  updateConfigFormValue(state, ["agents", "list", index, "skills"], [...next]);
                },
                onAgentSkillsClear: (agentId) => {
                  // Dynamic agents: clear skills array (use all)
                  if (isDynamicAgent(state, agentId)) {
                    void updateDynamicAgentField(state, agentId, "skills", []);
                    return;
                  }
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  removeConfigFormValue(state, ["agents", "list", index, "skills"]);
                },
                onAgentSkillsDisableAll: (agentId) => {
                  // Dynamic agents: set skills to a placeholder to represent "none enabled".
                  // In the DB, [] means "use all", so we use a dummy entry to signal an explicit
                  // empty allowlist. The config bridge (toConfigEntry) only emits skills when
                  // the array is non-empty, so this placeholder will create a config entry with
                  // skills: ["__disabled__"] which matches no real skill → effectively all disabled.
                  if (isDynamicAgent(state, agentId)) {
                    void updateDynamicAgentField(state, agentId, "skills", ["__disabled__"]);
                    return;
                  }
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  updateConfigFormValue(state, ["agents", "list", index, "skills"], []);
                },
                onModelChange: (agentId, modelId) => {
                  // Dynamic agents: update DB definition, not config file
                  if (isDynamicAgent(state, agentId)) {
                    // Preserve existing fallbacks when changing primary
                    const def = state.agentDefs.find((d) => d.agentId === agentId);
                    const existingFallbacks =
                      def?.model && typeof def.model === "object"
                        ? (def.model as { fallbacks?: string[] }).fallbacks
                        : undefined;
                    const modelObj = modelId
                      ? {
                          primary: modelId,
                          ...(Array.isArray(existingFallbacks) && existingFallbacks.length > 0
                            ? { fallbacks: existingFallbacks }
                            : {}),
                        }
                      : null;
                    void updateDynamicAgentField(state, agentId, "model", modelObj);
                    return;
                  }
                  if (!configValue) {
                    console.warn("[agents] model change ignored: config not loaded yet");
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    console.warn("[agents] model change ignored: agents.list not found in config");
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    console.warn(
                      `[agents] model change ignored: agent "${agentId}" not in agents.list`,
                    );
                    return;
                  }
                  const basePath = ["agents", "list", index, "model"];
                  if (!modelId) {
                    removeConfigFormValue(state, basePath);
                    return;
                  }
                  const entry = list[index] as { model?: unknown };
                  const existing = entry?.model;
                  if (existing && typeof existing === "object" && !Array.isArray(existing)) {
                    const fallbacks = (existing as { fallbacks?: unknown }).fallbacks;
                    const next = {
                      primary: modelId,
                      ...(Array.isArray(fallbacks) ? { fallbacks } : {}),
                    };
                    updateConfigFormValue(state, basePath, next);
                  } else {
                    updateConfigFormValue(state, basePath, modelId);
                  }
                },
                onModelFallbacksChange: (agentId, fallbacks) => {
                  // Dynamic agents: update DB model jsonb with fallbacks
                  if (isDynamicAgent(state, agentId)) {
                    const def = state.agentDefs.find((d) => d.agentId === agentId);
                    const existingPrimary =
                      def?.model && typeof def.model === "object"
                        ? (def.model as { primary?: string }).primary
                        : undefined;
                    const normalized = fallbacks.map((f) => f.trim()).filter(Boolean);
                    const modelObj = existingPrimary
                      ? {
                          primary: existingPrimary,
                          ...(normalized.length > 0 ? { fallbacks: normalized } : {}),
                        }
                      : normalized.length > 0
                        ? { fallbacks: normalized }
                        : null;
                    void updateDynamicAgentField(state, agentId, "model", modelObj);
                    return;
                  }
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "model"];
                  const entry = list[index] as { model?: unknown };
                  const normalized = fallbacks.map((name) => name.trim()).filter(Boolean);
                  const existing = entry.model;
                  const resolvePrimary = () => {
                    if (typeof existing === "string") {
                      return existing.trim() || null;
                    }
                    if (existing && typeof existing === "object" && !Array.isArray(existing)) {
                      const primary = (existing as { primary?: unknown }).primary;
                      if (typeof primary === "string") {
                        const trimmed = primary.trim();
                        return trimmed || null;
                      }
                    }
                    return null;
                  };
                  const primary = resolvePrimary();
                  if (normalized.length === 0) {
                    if (primary) {
                      updateConfigFormValue(state, basePath, primary);
                    } else {
                      removeConfigFormValue(state, basePath);
                    }
                    return;
                  }
                  const next = primary
                    ? { primary, fallbacks: normalized }
                    : { fallbacks: normalized };
                  updateConfigFormValue(state, basePath, next);
                },
                // Dynamic Agent Definitions callbacks
                onAgentDefsRefresh: () =>
                  loadAgentDefinitions(state as unknown as AgentDefinitionsState),
                onAgentDefCreate: () =>
                  startCreateAgentDef(state as unknown as AgentDefinitionsState),
                onAgentDefEdit: (def: AgentDefinitionEntry) =>
                  startEditAgentDef(state as unknown as AgentDefinitionsState, def),
                onAgentDefCancel: () =>
                  cancelAgentDefForm(state as unknown as AgentDefinitionsState),
                onAgentDefSave: () => {
                  const refreshSidebar = () => void loadAgents(state);
                  if (state.agentDefEditingId) {
                    void updateAgentDef(state as unknown as AgentDefinitionsState, refreshSidebar);
                  } else {
                    void createAgentDefinition(
                      state as unknown as AgentDefinitionsState,
                      refreshSidebar,
                    );
                  }
                },
                onAgentDefDelete: (agentId: string) => {
                  if (confirm("Delete this agent definition? This cannot be undone.")) {
                    void deleteAgentDefinition(
                      state as unknown as AgentDefinitionsState,
                      agentId,
                      () => void loadAgents(state),
                    );
                  }
                },
                onAgentDefToggle: (agentId: string, active: boolean) =>
                  toggleAgentDefinition(
                    state as unknown as AgentDefinitionsState,
                    agentId,
                    active,
                    () => void loadAgents(state),
                  ),
                onAgentDefFormChange: (field: string, value: unknown) =>
                  updateAgentDefForm(state as unknown as AgentDefinitionsState, field, value),
                // Bindings
                onAgentDefBindingAdd: () =>
                  addAgentDefBinding(state as unknown as AgentDefinitionsState),
                onAgentDefBindingRemove: (index: number) =>
                  removeAgentDefBinding(state as unknown as AgentDefinitionsState, index),
                onAgentDefBindingChange: (index: number, field: string, value: string) =>
                  updateAgentDefBinding(
                    state as unknown as AgentDefinitionsState,
                    index,
                    field,
                    value,
                  ),
                // Identity file overrides
                agentDefIdFiles: state.agentDefIdFiles,
                agentDefIdFilesLoading: state.agentDefIdFilesLoading,
                agentDefIdFileActive: state.agentDefIdFileActive,
                agentDefIdFileDrafts: state.agentDefIdFileDrafts,
                agentDefIdFileSaving: state.agentDefIdFileSaving,
                onAgentDefIdFileSelect: (fileName: string) =>
                  selectAgentDefIdentityFile(state as unknown as AgentDefinitionsState, fileName),
                onAgentDefIdFileDraftChange: (fileName: string, content: string) =>
                  updateAgentDefIdentityDraft(
                    state as unknown as AgentDefinitionsState,
                    fileName,
                    content,
                  ),
                onAgentDefIdFileSave: (agentId: string, fileName: string) =>
                  void saveAgentDefIdentityFile(
                    state as unknown as AgentDefinitionsState,
                    agentId,
                    fileName,
                  ),
                onAgentDefIdFileReset: (agentId: string, fileName: string) =>
                  void resetAgentDefIdentityFile(
                    state as unknown as AgentDefinitionsState,
                    agentId,
                    fileName,
                  ),
              })
            : nothing
        }

        ${
          state.tab === "skill-config"
            ? renderSkillsSettings({
                loading: state.skillsSettingsLoading,
                error: state.skillsSettingsError,
                skills: state.skillsSettingsList,
                selectedKey: state.skillsSettingsSelectedKey,
                params: state.skillsSettingsParams,
                paramsLoading: state.skillsSettingsParamsLoading,
                paramsError: state.skillsSettingsParamsError,
                edits: state.skillsSettingsEdits,
                saving: state.skillsSettingsSaving,
                saveMessage: state.skillsSettingsSaveMessage,
                onRefresh: () => loadSkillsSettingsList(state),
                onSelect: (key) => selectSkill(state, key),
                onEditParam: (paramName, value) => updateSkillParamEdit(state, paramName, value),
                onSaveParam: (skillKey, paramName) =>
                  saveSkillParameter(state, skillKey, paramName),
                onSaveAll: (skillKey) => saveAllSkillParameters(state, skillKey),
                onToggleEnabled: () => {},
              })
            : nothing
        }

        ${
          state.tab === "skills"
            ? renderSkills({
                loading: state.skillsLoading,
                report: state.skillsReport,
                error: state.skillsError,
                filter: state.skillsFilter,
                edits: state.skillEdits,
                messages: state.skillMessages,
                busyKey: state.skillsBusyKey,
                onFilterChange: (next) => (state.skillsFilter = next),
                onRefresh: () => {
                  loadSkills(state, { clearMessages: true });
                },
                onToggle: (key, enabled) => updateSkillEnabled(state, key, enabled),
                onToggleCommand: (key, command) => updateSkillCommand(state, key, command),
                onEdit: (key, value) => updateSkillEdit(state, key, value),
                onSaveKey: (key) => saveSkillApiKey(state, key),
                onInstall: (skillKey, name, installId) =>
                  installSkill(state, skillKey, name, installId),
                // Skill parameter configuration
                configSelectedKey: state.skillsSettingsSelectedKey,
                configParams: state.skillsSettingsParams,
                configParamsLoading: state.skillsSettingsParamsLoading,
                configParamsError: state.skillsSettingsParamsError,
                configEdits: state.skillsSettingsEdits,
                configSaving: state.skillsSettingsSaving,
                configSaveMessage: state.skillsSettingsSaveMessage,
                onConfigSelect: (key) => selectSkill(state, key),
                onConfigEditParam: (param, val) => updateSkillParamEdit(state, param, val),
                onConfigSaveParam: (sk, param) => saveSkillParameter(state, sk, param),
                onConfigSaveAll: (sk) => saveAllSkillParameters(state, sk),
              })
            : nothing
        }

        ${
          state.tab === "nodes"
            ? renderNodes({
                loading: state.nodesLoading,
                nodes: state.nodes,
                devicesLoading: state.devicesLoading,
                devicesError: state.devicesError,
                devicesList: state.devicesList,
                configForm:
                  state.configForm ??
                  (state.configSnapshot?.config as Record<string, unknown> | null),
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                configFormMode: state.configFormMode,
                execApprovalsLoading: state.execApprovalsLoading,
                execApprovalsSaving: state.execApprovalsSaving,
                execApprovalsDirty: state.execApprovalsDirty,
                execApprovalsSnapshot: state.execApprovalsSnapshot,
                execApprovalsForm: state.execApprovalsForm,
                execApprovalsSelectedAgent: state.execApprovalsSelectedAgent,
                execApprovalsTarget: state.execApprovalsTarget,
                execApprovalsTargetNodeId: state.execApprovalsTargetNodeId,
                onRefresh: () => loadNodes(state),
                onDevicesRefresh: () => loadDevices(state),
                onDeviceApprove: (requestId) => approveDevicePairing(state, requestId),
                onDeviceReject: (requestId) => rejectDevicePairing(state, requestId),
                onDeviceRotate: (deviceId, role, scopes) =>
                  rotateDeviceToken(state, { deviceId, role, scopes }),
                onDeviceRevoke: (deviceId, role) => revokeDeviceToken(state, { deviceId, role }),
                onLoadConfig: () => loadConfig(state),
                onLoadExecApprovals: () => {
                  const target =
                    state.execApprovalsTarget === "node" && state.execApprovalsTargetNodeId
                      ? { kind: "node" as const, nodeId: state.execApprovalsTargetNodeId }
                      : { kind: "gateway" as const };
                  return loadExecApprovals(state, target);
                },
                onBindDefault: (nodeId) => {
                  if (nodeId) {
                    updateConfigFormValue(state, ["tools", "exec", "node"], nodeId);
                  } else {
                    removeConfigFormValue(state, ["tools", "exec", "node"]);
                  }
                },
                onBindAgent: (agentIndex, nodeId) => {
                  const basePath = ["agents", "list", agentIndex, "tools", "exec", "node"];
                  if (nodeId) {
                    updateConfigFormValue(state, basePath, nodeId);
                  } else {
                    removeConfigFormValue(state, basePath);
                  }
                },
                onSaveBindings: () => saveConfig(state),
                onExecApprovalsTargetChange: (kind, nodeId) => {
                  state.execApprovalsTarget = kind;
                  state.execApprovalsTargetNodeId = nodeId;
                  state.execApprovalsSnapshot = null;
                  state.execApprovalsForm = null;
                  state.execApprovalsDirty = false;
                  state.execApprovalsSelectedAgent = null;
                },
                onExecApprovalsSelectAgent: (agentId) => {
                  state.execApprovalsSelectedAgent = agentId;
                },
                onExecApprovalsPatch: (path, value) =>
                  updateExecApprovalsFormValue(state, path, value),
                onExecApprovalsRemove: (path) => removeExecApprovalsFormValue(state, path),
                onSaveExecApprovals: () => {
                  const target =
                    state.execApprovalsTarget === "node" && state.execApprovalsTargetNodeId
                      ? { kind: "node" as const, nodeId: state.execApprovalsTargetNodeId }
                      : { kind: "gateway" as const };
                  return saveExecApprovals(state, target);
                },
              })
            : nothing
        }

        ${
          state.tab === "chat"
            ? renderChat({
                sessionKey: state.sessionKey,
                onSessionKeyChange: (next) => {
                  state.sessionKey = next;
                  state.chatMessage = "";
                  state.chatAttachments = [];
                  state.chatStream = null;
                  state.chatStreamStartedAt = null;
                  state.chatRunId = null;
                  state.chatQueue = [];
                  state.resetToolStream();
                  state.resetChatScroll();
                  state.applySettings({
                    ...state.settings,
                    sessionKey: next,
                    lastActiveSessionKey: next,
                  });
                  void state.loadAssistantIdentity();
                  void loadChatHistory(state);
                  void refreshChatAvatar(state);
                },
                thinkingLevel: state.chatThinkingLevel,
                showThinking,
                loading: state.chatLoading,
                sending: state.chatSending,
                compactionStatus: state.compactionStatus,
                assistantAvatarUrl: chatAvatarUrl,
                messages: state.chatMessages,
                toolMessages: state.chatToolMessages,
                stream: state.chatStream,
                streamStartedAt: state.chatStreamStartedAt,
                draft: state.chatMessage,
                queue: state.chatQueue,
                connected: state.connected,
                canSend: state.connected,
                disabledReason: chatDisabledReason,
                error: state.lastError,
                sessions: state.sessionsResult,
                focusMode: chatFocus,
                onRefresh: () => {
                  state.resetToolStream();
                  return Promise.all([loadChatHistory(state), refreshChatAvatar(state)]);
                },
                onToggleFocusMode: () => {
                  if (state.onboarding) {
                    return;
                  }
                  state.applySettings({
                    ...state.settings,
                    chatFocusMode: !state.settings.chatFocusMode,
                  });
                },
                onChatScroll: (event) => state.handleChatScroll(event),
                onDraftChange: (next) => (state.chatMessage = next),
                attachments: state.chatAttachments,
                onAttachmentsChange: (next) => (state.chatAttachments = next),
                onSend: () => state.handleSendChat(),
                canAbort: Boolean(state.chatRunId),
                onAbort: () => void state.handleAbortChat(),
                onQueueRemove: (id) => state.removeQueuedMessage(id),
                onNewSession: async () => {
                  const projectId = state.chatProjectId as string | undefined;
                  if (projectId) {
                    const switchSession = makeSwitchSession(state);
                    const sk = await createPinnedProjectSession(
                      state as unknown as PinState,
                      projectId,
                    );
                    if (sk) switchSession(sk);
                  } else {
                    state.handleSendChat("/new", { restoreDraft: true });
                  }
                },
                onResetSession: () => state.handleSendChat("/new", { restoreDraft: true }),
                showNewMessages: state.chatNewMessagesBelow && !state.chatManualRefreshInFlight,
                onScrollToBottom: () => state.scrollToBottom(),
                // Sidebar props for tool output viewing
                sidebarOpen: state.sidebarOpen,
                sidebarContent: state.sidebarContent,
                sidebarError: state.sidebarError,
                splitRatio: state.splitRatio,
                onOpenSidebar: (content: string) => state.handleOpenSidebar(content),
                onCloseSidebar: () => state.handleCloseSidebar(),
                onSplitRatioChange: (ratio: number) => state.handleSplitRatioChange(ratio),
                assistantName: state.assistantName,
                assistantAvatar: state.assistantAvatar,
                // Slash-command autocomplete
                commands: state.chatCommands,
                cmdMenuOpen: state.chatCmdMenuOpen,
                cmdIndex: state.chatCmdIndex,
                onCmdMenuOpen: (filter: string) => {
                  // Recompute filtered index when filter changes
                  const lower = filter.toLowerCase();
                  const matches = state.chatCommands.filter(
                    (c) => !lower || c.name.toLowerCase().startsWith(lower) || c.name.toLowerCase().includes(lower),
                  );
                  const currentName = matches[state.chatCmdIndex]?.name;
                  const nextIdx = currentName
                    ? Math.max(0, matches.findIndex((c) => c.name === currentName))
                    : 0;
                  state.chatCmdMenuOpen = true;
                  state.chatCmdIndex = nextIdx;
                },
                onCmdMenuClose: () => {
                  state.chatCmdMenuOpen = false;
                  state.chatCmdIndex = 0;
                },
                onCmdNav: (delta: number) => {
                  const filter = (/^\/(\S*)$/.exec(state.chatMessage) ?? [])[1] ?? "";
                  const lower = filter.toLowerCase();
                  const matches = state.chatCommands.filter(
                    (c) => !lower || c.name.toLowerCase().startsWith(lower) || c.name.toLowerCase().includes(lower),
                  );
                  if (!matches.length) return;
                  const len = matches.length;
                  state.chatCmdIndex = ((state.chatCmdIndex + delta) % len + len) % len;
                },
                onCmdSelect: (name: string) => {
                  state.chatMessage = `/${name} `;
                  state.chatCmdMenuOpen = false;
                  state.chatCmdIndex = 0;
                },
                consoleVisible: state.chatConsoleVisible,
                consoleSkills: state.chatConsoleSkills,
                onToggleConsole: () => {
                  state.chatConsoleVisible = !state.chatConsoleVisible;
                },
                onConsoleSkillClick: (skillName: string) => {
                  void state.handleSendChat(`/${skillName}`);
                },
                previewFilePath: state.previewFilePath,
                previewFileName: state.previewFileName,
                previewFileType: state.previewFileType,
                onOpenFilePreview: (filePath: string) => {
                  state.previewFilePath = filePath;
                  state.previewFileName = filePath.split("/").pop() ?? filePath;
                  state.previewFileType = resolveFileType(filePath);
                  state.sidebarOpen = true;
                },
                onOpenFileFolder: (filePath: string) => {
                  const token = state.settings.token ?? "";
                  void fetch("/api/files/open-folder", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ path: filePath, token }),
                  }).catch(() => {});
                },
                onClosePreview: () => {
                  state.previewFilePath = null;
                  state.previewFileName = null;
                  state.previewFileType = null;
                  state.sidebarOpen = false;
                },
                token: state.settings.token ?? "",
              })
            : nothing
        }

        ${
          state.tab === "security"
            ? renderSecurityAdmin({
                configForm: configValue,
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                connected: state.connected,
                onConfigPatch: (path, value) => updateConfigFormValue(state, path, value),
                onConfigRemove: (path) => removeConfigFormValue(state, path),
                onConfigSave: () => saveConfig(state),
                onConfigReload: () => loadConfig(state),
              })
            : nothing
        }

        ${
          state.tab === "settings"
            ? renderConfig({
                raw: state.configRaw,
                originalRaw: state.configRawOriginal,
                valid: state.configValid,
                issues: state.configIssues,
                loading: state.configLoading,
                saving: state.configSaving,
                applying: state.configApplying,
                updating: state.updateRunning,
                connected: state.connected,
                schema: state.configSchema,
                schemaLoading: state.configSchemaLoading,
                uiHints: state.configUiHints,
                formMode: state.configFormMode,
                formValue: state.configForm,
                originalValue: state.configFormOriginal,
                searchQuery: state.settingsSearchQuery,
                activeSection: state.settingsActiveSection,
                activeSubsection: state.settingsActiveSubsection,
                sectionFilter: SETTINGS_SECTIONS,
                sidebarTitle: "Settings",
                configSectionsCollapsed: state.settingsSectionsCollapsed,
                onRawChange: (next) => {
                  state.configRaw = next;
                },
                onFormModeChange: (mode) => (state.configFormMode = mode),
                onFormPatch: (path, value) => updateConfigFormValue(state, path, value),
                onSearchChange: (query) => (state.settingsSearchQuery = query),
                onSectionChange: (section) => {
                  state.settingsActiveSection = section;
                  state.settingsActiveSubsection = null;
                },
                onSubsectionChange: (section) => (state.settingsActiveSubsection = section),
                onToggleConfigSection: (sectionKey) => {
                  const next = { ...state.settingsSectionsCollapsed };
                  next[sectionKey] = !next[sectionKey];
                  state.settingsSectionsCollapsed = next;
                },
                onReload: () => loadConfig(state),
                onSave: () => saveConfig(state),
                onApply: () => applyConfig(state),
                onUpdate: () => runUpdate(state),
              })
            : nothing
        }

        ${
          state.tab === "config"
            ? renderConfig({
                raw: state.configRaw,
                originalRaw: state.configRawOriginal,
                valid: state.configValid,
                issues: state.configIssues,
                loading: state.configLoading,
                saving: state.configSaving,
                applying: state.configApplying,
                updating: state.updateRunning,
                connected: state.connected,
                schema: state.configSchema,
                schemaLoading: state.configSchemaLoading,
                uiHints: state.configUiHints,
                formMode: state.configFormMode,
                formValue: state.configForm,
                originalValue: state.configFormOriginal,
                searchQuery: state.configSearchQuery,
                activeSection: state.configActiveSection,
                activeSubsection: state.configActiveSubsection,
                sectionExclude: CONFIG_EXCLUDED_SECTIONS,
                sidebarTitle: "Config",
                configSectionsCollapsed: state.configSectionsCollapsed,
                onRawChange: (next) => {
                  state.configRaw = next;
                },
                onFormModeChange: (mode) => (state.configFormMode = mode),
                onFormPatch: (path, value) => updateConfigFormValue(state, path, value),
                onSearchChange: (query) => (state.configSearchQuery = query),
                onSectionChange: (section) => {
                  state.configActiveSection = section;
                  state.configActiveSubsection = null;
                },
                onSubsectionChange: (section) => (state.configActiveSubsection = section),
                onToggleConfigSection: (sectionKey) => {
                  const next = { ...state.configSectionsCollapsed };
                  next[sectionKey] = !next[sectionKey];
                  state.configSectionsCollapsed = next;
                },
                onReload: () => loadConfig(state),
                onSave: () => saveConfig(state),
                onApply: () => applyConfig(state),
                onUpdate: () => runUpdate(state),
              })
            : nothing
        }

        ${
          state.tab === "debug"
            ? renderDebug({
                loading: state.debugLoading,
                status: state.debugStatus,
                health: state.debugHealth,
                lifecheck: state.debugLifecheck,
                eventLog: state.eventLog,
                callMethod: state.debugCallMethod,
                callParams: state.debugCallParams,
                callResult: state.debugCallResult,
                callError: state.debugCallError,
                onCallMethodChange: (next) => (state.debugCallMethod = next),
                onCallParamsChange: (next) => (state.debugCallParams = next),
                onRefresh: () => loadDebug(state),
                onCall: () => callDebugMethod(state),
                onClearEventLog: () => {
                  state.eventLog = [];
                  state.eventLogBuffer = [];
                },
              })
            : nothing
        }

        ${
          state.tab === "projects"
            ? renderProjects(buildProjectsProps(state as unknown as IgloobotState))
            : nothing
        }

        ${
          state.tab === "tasks"
            ? renderTasksAdmin(buildTasksProps(state as unknown as IgloobotState))
            : nothing
        }

        ${
          state.tab === "users"
            ? renderUsersAdmin(buildUsersProps(state as unknown as IgloobotState))
            : nothing
        }

        ${
          state.tab === "mcp-skills"
            ? renderSkillsAdmin(buildMcpSkillsProps(state as unknown as IgloobotState))
            : nothing
        }

        ${
          state.tab === "models"
            ? renderModelsAdmin(buildModelsProps(state as unknown as ModelsState))
            : nothing
        }

        ${
          state.tab === "avatar"
            ? renderAvatarAdmin(
                buildAvatarProps(state as unknown as IgloobotState, state.modelCatalog),
              )
            : nothing
        }

        ${
          state.tab === "company"
            ? renderCompany(buildCompanyProps(state as unknown as IgloobotState))
            : nothing
        }

        ${
          state.tab === "email"
            ? renderEmail(buildEmailProps(state as unknown as IgloobotState))
            : nothing
        }

        ${
          state.tab === "n8n"
            ? renderN8nAdmin(buildN8nAdminProps(state as unknown as N8nState))
            : nothing
        }

        ${
          state.tab === "pipelines"
            ? renderPipelinesAdmin(buildPipelinesProps(state as unknown as PipelinesState))
            : nothing
        }

        ${
          state.tab === "documents"
            ? renderDocumentsAdmin(buildDocumentsProps(state as unknown as DocumentsState))
            : nothing
        }

        ${
          state.tab === "canvas"
            ? renderCanvasView({
                gatewayUrl: `${location.protocol}//${location.host}`,
              })
            : nothing
        }

        ${
          state.tab === "computer"
            ? renderComputerAdmin({
                status: state.computerStatus,
                config: state.computerConfig,
                loading: state.computerLoading,
                error: state.computerError,
                screenshot: state.computerScreenshot,
                screenshotLoading: state.computerScreenshotLoading,
                onRefresh: () => loadComputerStatus(state as unknown as ComputerState),
                onRefreshConfig: () => loadComputerConfig(state as unknown as ComputerState),
                onScreenshot: () => takeScreenshot(state as unknown as ComputerState),
              })
            : nothing
        }

        ${
          state.tab === "system-overview"
            ? renderSystemOverview({
                loading: state.systemOverviewLoading,
                data: state.systemOverviewData,
                error: state.systemOverviewError,
                subView: state.systemOverviewSubView,
                onSubViewChange: (view) => {
                  state.systemOverviewSubView = view;
                },
                onRefresh: () => loadSystemOverview(state),
              })
            : nothing
        }

        ${
          state.tab === "agent-plans"
            ? renderAgentPlans({
                loading: state.agentPlansLoading,
                plans: state.agentPlans,
                error: state.agentPlansError,
                statusFilter: state.agentPlansStatusFilter,
                onStatusFilterChange: (filter) => {
                  state.agentPlansStatusFilter = filter;
                  void loadAgentPlans(state);
                },
                onRefresh: () => void loadAgentPlans(state),
              })
            : nothing
        }

        ${
          state.tab === "user-settings"
            ? renderUserSettings({
                loading: state.tabVisibilityLoading,
                saving: state.tabVisibilitySaving,
                visibilityMap: state.tabVisibilityMap,
                dirty: state.tabVisibilityDirty,
                error: state.tabVisibilityError,
                onToggle: (role, tabKey, visible) => {
                  toggleTabVisible(state, role, tabKey, visible);
                },
                onSave: () => saveTabVisibility(state),
                onRefresh: () => loadTabVisibility(state),
                roleSettingsMap: state.roleSettingsMap,
                roleSettingsDirty: state.roleSettingsDirty,
                onRoleSettingToggle: (role, key, value) => {
                  setRoleSettingValue(state, role, key, value);
                },
                onRoleSettingsSave: () => saveRoleSettings(state),
              })
            : nothing
        }

        ${
          state.tab === "inference-config"
            ? renderInferenceConfig({
                loading: state.inferenceLoading,
                saving: state.inferenceSaving,
                entries: state.inferenceEntries,
                dirty: state.inferenceDirty,
                error: state.inferenceError,
                onUpdate: (provider, modelId, field, value) =>
                  updateModelPricing(state, provider, modelId, field, value),
                onSave: () => saveInferenceConfig(state),
                onRefresh: () => loadInferenceConfig(state),
                maintenanceFeeCents: state.billingMaintenanceFeeCents,
                selectedPlanId: state.billingSelectedPlanId,
                extraPerToken: state.billingExtraPerToken,
                plans: state.billingPlans,
                billingDirty: state.billingDirty,
                onMaintenanceFeeChange: (cents) => updateMaintenanceFee(state, cents),
                onPlanChange: (planId) => updateSelectedPlan(state, planId),
                onExtraPerTokenToggle: (enabled) => updateExtraPerToken(state, enabled),
                onBillingSave: () => saveBillingConfig(state),
              })
            : nothing
        }

        ${
          state.tab === "logs"
            ? renderLogs({
                loading: state.logsLoading,
                error: state.logsError,
                file: state.logsFile,
                entries: state.logsEntries,
                filterText: state.logsFilterText,
                levelFilters: state.logsLevelFilters,
                autoFollow: state.logsAutoFollow,
                truncated: state.logsTruncated,
                onFilterTextChange: (next) => (state.logsFilterText = next),
                onLevelToggle: (level, enabled) => {
                  state.logsLevelFilters = { ...state.logsLevelFilters, [level]: enabled };
                },
                onToggleAutoFollow: (next) => (state.logsAutoFollow = next),
                onRefresh: () => loadLogs(state, { reset: true }),
                onExport: (lines, label) => state.exportLogs(lines, label),
                onScroll: (event) => state.handleLogsScroll(event),
              })
            : nothing
        }
      </main>
      ${renderExecApprovalPrompt(state)}
      ${renderGatewayUrlConfirmation(state)}
    </div>
  `;
}
